import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import FilledInput from '@material-ui/core/FilledInput';
import InputLabel from '@material-ui/core/InputLabel';
import Container from '@material-ui/core/Container';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

// Interfaces
import { sendCommandResponse } from '../store/Alerts/AlertsActions';
import { UpdateWeightAlertRecords } from '../Interfaces/Alerts/CommandResponse';
import {
  WeightAlertGroupRecord,
  WeightAlertRecord,
} from '../Interfaces/Alerts/AlertRecord';
import { UPDATE_WGT_RECORDS_API } from '../Interfaces/Alerts/Api';
import { GetString } from './StringResourceManager';
import { ApplicationState } from '../store';
import { fetchAlertByID } from './AlertsGrid/AlertsGrid';

const useStyles = makeStyles(theme => ({
  gridAlign: {
    display: 'flex',
    alignItems: 'center',
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: '25ch',
  },
  box: {
    height: 60,
    display: 'flex',
    padding: theme.spacing(2),
    margin: theme.spacing(1),
  },
  spreadBox: {
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  dialogAction: {
    height: '3rem',
    padding: theme.spacing(3),
  },
  dialogLabel: {
    fontSize: '1.1rem',
    display: 'inline',
  },
  dialogTextBold: {
    fontWeight: 'bold',
  },
  breakWord: {
    wordWrap: 'break-word',
  },
}));
interface Props {
  alert: WeightAlertGroupRecord;
  closeContainer: () => void;
}

const UpdateWghtRecordsForm: React.FC<Props> = ({ alert, closeContainer }) => {
  const classes = useStyles();
  const [weight, setWeight] = useState<number>(alert.ReferenceWeight);
  const [fixedWeight, setFixedWeight] = useState<boolean>(alert.FixedWeight);
  const [referenceAlert, setReferenceAlert] =
    useState<WeightAlertRecord>(Object);

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  const dispatch: Dispatch<any> = useDispatch();

  useEffect((): ReturnType<React.EffectCallback> => {
    if (alert.AlertIDs.length > 0) {
      fetchAlertByID(alert.AlertIDs[0], token).then(data => {
        setReferenceAlert(data);
      });
    }
    return (): void => {};
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // Preventing the page from reloading
    event.preventDefault();

    let payload: UpdateWeightAlertRecords = {
      WeightRecords: [],
    };

    alert.AlertIDs.forEach(alertID => {
      payload.WeightRecords.push({
        AlertID: alertID,
        Weight: weight ?? -1,
        FixedWeight: fixedWeight,
      });
    });

    sendCommandResponse(dispatch, UPDATE_WGT_RECORDS_API, payload);
  };

  const handleWeightChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeight(Number(event.currentTarget.value));
  };

  const handleFixedWeightChanged = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFixedWeight(event.target.checked);
  };

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit}>
      <Container>
        <Grid
          container
          spacing={2}
          className={clsx(classes.gridAlign, classes.margin)}
        >
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_ITEM_NAME')}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
            >
              {alert.ItemDesc}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_ITEM_BARCODE')}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(
                classes.dialogLabel,
                classes.dialogTextBold,
                classes.breakWord,
                classes.textField
              )}
              gutterBottom
            >
              {alert.Barcode}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_WGTS')}(
              {GetString('UPDATE_WGT_RECORD_STANDARD_WGT_UNIT')}):{' '}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(
                classes.dialogLabel,
                classes.dialogTextBold,
                classes.breakWord,
                classes.textField
              )}
              gutterBottom
            >
              {referenceAlert?.WeightRecord?.join(', ') ?? ''}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Divider />
          </Grid>

          <Grid item md={6} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_STANDARD_WGT')}
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControl
              className={clsx(classes.margin, classes.textField)}
              variant="outlined"
              disabled
            >
              <InputLabel>
                {alert.ReferenceWeight >= 0 ? alert.ReferenceWeight : 'N/A'}
              </InputLabel>
              <FilledInput
                endAdornment={
                  <InputAdornment position="end">
                    {GetString('UPDATE_WGT_RECORD_STANDARD_WGT_UNIT')}
                  </InputAdornment>
                }
              />
            </FormControl>
          </Grid>

          <Grid item md={6} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_STANDARD_SET_WGT')}
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControl
              className={clsx(classes.margin, classes.textField)}
              variant="outlined"
            >
              <OutlinedInput
                value={weight}
                onChange={handleWeightChanged}
                endAdornment={
                  <InputAdornment position="end">
                    {GetString('UPDATE_WGT_RECORD_STANDARD_WGT_UNIT')}
                  </InputAdornment>
                }
                placeholder="Weight"
                type="number"
              />
            </FormControl>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={handleFixedWeightChanged}
                  name="checkedB"
                  color="primary"
                  checked={fixedWeight}
                />
              }
              label={GetString('UPDATE_WGT_RECORD_STANDARD_SET_FIXED_WGT')}
            />
          </Grid>
        </Grid>
        <Divider />
        <Box className={clsx(classes.spreadBox, classes.box)}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            className={classes.dialogAction}
            onClick={closeContainer}
          >
            {GetString('UPDATE_WGT_RECORD_ACTION_1')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.dialogAction}
            onClick={closeContainer}
          >
            {GetString('UPDATE_WGT_RECORD_ACTION_2')}
          </Button>
        </Box>
      </Container>
    </form>
  );
};

export default UpdateWghtRecordsForm;
